<template>
  <div class="spending-limit-chart">
    <div class="header">
      <h3>
        <v-icon :size="24">mdi-finance</v-icon>
        {{ t('title') }}
      </h3>
      <p>{{ t('description') }}</p>
    </div>
    <LoadingTable v-if="loading" />
    <div v-else>
      <p>
        <strong>{{ t('billing_cycle') }}</strong>
      </p>
      <div class="store-list">
        <div
          v-for="store in userStores"
          :key="store.id"
          class="store-list-item"
          :class="{ error: isStoreActionNeeded(store) }"
        >
          <div class="store-list-item-header">
            <div class="store-list-item-name">
              <strong>{{ store.name }}</strong>
            </div>
            <div class="action">
              <div class="limit">
                <span class="limit-current">
                  ${{ formatNumberByLocale(store.spendingLimit?.used ?? 0) }}
                </span>
                /
                <span class="limit-total">
                  ${{ formatNumberByLocale(store.spendingLimit?.activeLimit ?? 0) }}
                </span>
              </div>
              <button @click="editStore(store)" class="ml-2">
                <v-icon :size="24">mdi-pencil-outline</v-icon>
              </button>
            </div>
          </div>
          <gs-alert-bar
            v-if="isStoreActionNeeded(store)"
            @click="store.spendingLimit?.pendingLimit ? goToShopifyUrl(store) : editStore(store)"
            class="mb-3"
            type="warning"
            :message="
              !store.spendingLimit?.pendingLimit && !store.spendingLimit?.activeLimit
                ? t('billing_blocked')
                : store.spendingLimit?.pendingLimit
                ? t('approve_payment')
                : t('update_limit')
            "
          />
          <v-progress-linear
            class="store-list-item-progress"
            bg-color="beige"
            :color="isStoreActionNeeded(store) ? '#ff2424' : '#47D18F'"
            rounded
            height="29"
            :model-value="getProgressValue(store)"
          >
            <template v-slot>
              <strong> {{ Math.floor(getProgressValue(store)) }}% </strong>
            </template>
          </v-progress-linear>
        </div>
      </div>
      <p class="mt-5">
        <strong>{{ t('limit_hit_email') }}</strong>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import LoadingTable from '@/components/tools/Table/LoadingTable.vue'

import type { Integration } from '@/store/integrations'
import { Utils } from '@/helpers/mixins/utilsMixin'
import { IntegrationsMixin } from '@/helpers/mixins/integrationsMixin'

export default defineComponent({
  name: 'SpendingLimitChart',
  mixins: [Utils, IntegrationsMixin],
  components: {
    LoadingTable,
  },
  data() {
    return {
      loading: false,
    } as {
      loading: boolean
    }
  },
  async created() {
    this.loading = true
    await this.setIntegrations()
    this.loading = false
  },
  computed: {
    userStores(): Integration[] {
      return this.$store.getters['getIntegrationsByPlatform']('shopify')
    },
  },
  methods: {
    t(key: string, params?: { [key: string]: string | number }) {
      return this.$t(`SpendingLimitChart.${key}`, params ?? {})
    },
    isStoreActionNeeded(store: Integration) {
      return (
        this.getProgressValue(store) >= 100 ||
        (!store.spendingLimit?.activeLimit && !store.spendingLimit?.pendingLimit) ||
        store.spendingLimit?.pendingLimit ||
        store.spendingLimit?.isLimitReached ||
        (store.spendingLimit?.activeLimit &&
          !store.spendingLimit?.pendingLimit &&
          !store.spendingLimit?.isPrimaryStore &&
          this.userStores.length > 1 &&
          store.spendingLimit?.activeLimit < (store.spendingLimit?.min ?? 0))
      )
    },
    async editStore(store: Integration) {
      await this.$router.push({
        name: 'ShopifyCustomSpendingLimit',
        query: {
          store: store.id,
        },
      })
    },
    getProgressValue(store: Integration): number {
      return store.spendingLimit
        ? ((store.spendingLimit?.used ?? 0) / (store.spendingLimit?.activeLimit ?? 0)) * 100 || 0
        : 0
    },
    goToShopifyUrl(store: Integration) {
      window.open(store.spendingLimit?.pendingLimitUrl, '_self')
    },
  },
})
</script>

<style lang="scss" scoped>
@import '~vuetify/settings';

.spending-limit-chart {
  font-size: 18px;

  p {
    margin-bottom: 0;
  }
}

.gs-alert-bar {
  :deep(a) {
    color: var(--ui-blue);
    text-decoration: underline;
    cursor: pointer;
  }
}

.header {
  border-bottom: 1px solid var(--gray-light-CC);
  padding-bottom: 24px;
  margin-bottom: 16px;

  h3 {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 16px;

    .v-icon {
      margin-right: 8px;
    }
  }
}

.store-list {
  margin-top: 16px;
}

.store-list-item-name {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 0;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    flex-direction: row;
    align-items: center;
    flex: none;
  }
}

.store-list-item {
  &.error {
    .store-list-item-name {
      strong {
        color: var(--red);
      }
    }

    .limit-total {
      color: var(--red);
    }
  }

  &:not(:last-child) {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--gray-light-CC);
  }
}

.store-list-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    align-items: center;
  }

  .action {
    display: flex;
    gap: 4px;
  }

  .limit {
    font-size: 18px;
    font-weight: bold;
    color: var(--gray-light-7C);
  }

  .limit-total {
    color: var(--ui-black);
  }
}

.store-list-item-progress {
  :deep(.v-progress-linear__background) {
    opacity: 1;
  }

  :deep(.v-progress-linear__determinate) {
    border-radius: 40px;
  }

  :deep(.v-progress-linear__content) {
    font-size: 12px;
    font-weight: bold;
  }
}
</style>
